import { Badge, Box, Button, Flex, Menu, Portal } from '@chakra-ui/react';
import { BrandLogo } from './BrandLogo';
import { FiChevronLeft, FiHome, FiLogIn, FiLogOut, FiMenu } from 'react-icons/fi';
import { LOGIN_ENABLED } from '../config';
import { LuNotepadText, LuPanelTop } from 'react-icons/lu';
import type { NavConfig, NavItem } from './navigationData';
import { NavLink } from 'react-router-dom';
import { NavTabs } from './NavTabs';
import { Socials } from './Socials';

type MobileHeaderProps = {
    navItems: NavItem[];
    activeTab: string;
    user: any;
    isAdmin: boolean;
    userLoading: boolean;
    locationPathname: string;
    config: NavConfig;
    navigate: (path: string) => void;
    handleLogout: () => Promise<void>;
};

const MenuShell = ({ trigger, children }: { trigger: React.ReactElement; children: React.ReactNode }) => (
    <Menu.Root>
        <Menu.Trigger asChild>{trigger}</Menu.Trigger>
        <Portal>
            <Menu.Positioner>
                <Menu.Content>{children}</Menu.Content>
            </Menu.Positioner>
        </Portal>
    </Menu.Root>
);

export function MobileHeader({ navItems, activeTab, user, isAdmin, userLoading, locationPathname, config, navigate, handleLogout }: MobileHeaderProps) {
    return (
        <>
            <Flex px={4} alignItems="center">
                <BrandLogo />
                <Socials/>

                {config.showBackButton ? (
                    <Button size="sm" variant="ghost" colorScheme="orange" onClick={() => navigate('/')}>
                        <FiChevronLeft />
                    </Button>
                ) : (
                    <Flex display={{ base: 'flex', lg: 'none' }} ml="auto">
                        <MenuShell
                            trigger={<Button size="sm" variant="ghost" colorScheme="orange"><FiMenu /></Button>}
                        >
                            {config.showNavItemsInMenu && navItems.map((item) => (
                                <Menu.Item key={item.href} value={item.href} asChild>
                                    {item.href.startsWith('#') ? (
                                        <a href={item.href}>
                                            <Flex align="center" gap={2}>
                                                <span>{item.label}</span>
                                                {typeof item.badgeValue === 'number' && (
                                                    <Badge colorPalette="orange" variant="subtle" borderRadius="full" px={2} fontSize="xs">
                                                        {item.badgeValue}
                                                    </Badge>
                                                )}
                                            </Flex>
                                        </a>
                                    ) : (
                                        <NavLink to={item.href}>
                                            <Flex align="center" gap={2}>
                                                <span>{item.label}</span>
                                                {typeof item.badgeValue === 'number' && (
                                                    <Badge colorPalette="orange" variant="subtle" borderRadius="full" px={2} fontSize="xs">
                                                        {item.badgeValue}
                                                    </Badge>
                                                )}
                                            </Flex>
                                        </NavLink>
                                    )}
                                </Menu.Item>
                            ))}

                            {(user && locationPathname === '/') ? (
                                <>
                                    <Menu.Separator mb={0} />
                                    <Menu.Item mx={-1.5} py={2.5} px={3.5} w="auto" value="panel" asChild bg='orange.500' color='white' fontWeight='bold'>
                                        <NavLink to={isAdmin ? '/admin' : '/panel'}>
                                            <LuPanelTop/>
                                            {isAdmin ? 'Admin panel' : 'Panel klienta'}
                                        </NavLink>
                                    </Menu.Item>
                                </>
                            ) : (config.showNavItemsInMenu && <Menu.Separator />)}

                            {(locationPathname.startsWith('/admin') || locationPathname.startsWith('/panel')) && (
                                <Menu.Item value="homepage" fontWeight='bold' asChild>
                                    <NavLink to="/?noRedirectToPanel">
                                        <FiHome />
                                        Strona główna
                                    </NavLink>
                                </Menu.Item>
                            )}

                            <Menu.Item value="enroll" color='orange.700' fontWeight='bold' asChild>
                                <NavLink to="/zapisy">
                                    <LuNotepadText />
                                    Zapisy
                                </NavLink>
                            </Menu.Item>

                            {LOGIN_ENABLED && !userLoading && !user && locationPathname !== '/login' && (
                                <Menu.Item value="login" fontWeight='bold' asChild>
                                    <NavLink to="/login">
                                        <FiLogIn />
                                        Zaloguj się
                                    </NavLink>
                                </Menu.Item>
                            )}

                            {!userLoading && user && (
                                <Menu.Item onSelect={handleLogout} fontWeight='bold' color="gray.600" value="logout">
                                    <FiLogOut />
                                    <Box>Wyloguj</Box>
                                </Menu.Item>
                            )}
                        </MenuShell>
                    </Flex>
                )}
            </Flex>

            {config.showTabs && config.showTabsOnMobile && (
                <Flex alignItems="center" bg="white" flexWrap="wrap">
                    <NavTabs
                        navItems={navItems}
                        activeTab={activeTab}
                        size="md"
                        display="flex"
                        justifyContent={config.tabsAlign}
                    />
                </Flex>
            )}
        </>
    );
}
